'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { CalculatorDebugModal } from '@/components/calculator/CalculatorDebugModal';
import { ShiftKeyTooltip } from '@/components/ui/ShiftKeyTooltip';
import { useShiftKeyTooltips } from '@/hooks/useShiftKeyTooltips';
import { Bug, FileText, Building2, Home } from 'lucide-react';
import type { CalculatorInputs, PricingConfig, CalculationResults } from '@/types/calculator';
import type { SupportedCurrency } from '@/lib/env';

interface FooterProps {
  inputs?: CalculatorInputs;
  pricing?: PricingConfig;
  results?: CalculationResults | null;
  currency?: SupportedCurrency;
  showDebugModal?: boolean;
  showChangelog?: boolean;
  showHomeLink?: boolean;
}

export function Footer({ 
  inputs, 
  pricing,
  results, 
  currency,
  showDebugModal = false,
  showChangelog = true,
  showHomeLink = false
}: FooterProps) {
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const showShiftTooltips = useShiftKeyTooltips();

  return (
    <>
      <footer className="border-t border-border bg-card/50 backdrop-blur-sm mt-16">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo and Copyright */}
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-6 h-6 rounded bg-accessnow-orange text-white">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="text-sm text-muted-foreground">
                © 2025 <span className="text-accessnow-orange font-medium">AccessNow</span> ROI Calculator
              </div>
            </div>

            {/* Links */}
            <div className="flex items-center gap-4">
              {showHomeLink && (
                <Link href="/">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Calculator
                  </Button>
                </Link>
              )}
              
              {showDebugModal && (
                <ShiftKeyTooltip 
                  shortcut="D" 
                  show={showShiftTooltips}
                  position="top"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDebugModalOpen(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Bug className="h-4 w-4 mr-2" />
                    Calculator Debug
                  </Button>
                </ShiftKeyTooltip>
              )}
              
              {showChangelog && (
                <Link href="/changelog">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Changelog
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Debug Modal */}
      {showDebugModal && inputs && currency && (
        <CalculatorDebugModal
          open={debugModalOpen}
          onOpenChange={setDebugModalOpen}
          inputs={inputs}
          pricing={pricing}
          results={results ?? null}
          currency={currency}
        />
      )}
    </>
  );
}